// AvisoPagoDialog.jsx
//
// Cobro programado por cuenta (#1809). Sebastián lo activa por médico desde /admin/users; el
// backend decide la fase (`/api/user/me/` → `aviso_pago`, billing.aviso.aviso_de) y acá solo se
// dibuja. Dos fases usan este popup:
//   - `aviso`: antes del día de pago. Se cierra con "Ahora no" y vuelve al día siguiente.
//   - `dia_de_pago`: el día de pago sin cobro confirmado. Se cierra, pero vuelve en cada ingreso,
//     con la hora del corte.
// El bloqueo (día siguiente al de pago) NO es este popup: es la pantalla de `SuscripcionGate`.
// Contrato: .claude/progress/1809-aviso-pago/CONTRATO.md.
import { useEffect, useRef } from "react";
import { Box, Button, Dialog, DialogContent, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AccessAlarmOutlinedIcon from "@mui/icons-material/AccessAlarmOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PlanesYPago from "./PlanesYPago";
import JornadasAlMes from "./JornadasAlMes";
import { botonGhostSx, botonTextoSx } from "./botonesSx";
import { enviarEventoAviso } from "../../helpers/aviso-pago-api";
import { WHATSAPP_SOPORTE } from "../../helpers/soporte";
import useAhora from "../../hooks/useAhora";

export const fechaDiaMes = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("es-CL", { day: "numeric", month: "long" });
  } catch {
    return iso;
  }
};

// Cuánto falta para el corte, para MOSTRAR. El estado (cortado o no) lo decide el backend
// comparando instantes; esto nunca decide nada (regla de vencimientos de rules/frontend.md).
export const faltaPara = (isoHasta, ahora = Date.now()) => {
  const ms = new Date(isoHasta).getTime() - ahora;
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60);
  if (h >= 24) return `${Math.floor(h / 24)} d ${h % 24} h`;
  return h > 0 ? `${h} h ${min % 60} min` : `${min} min`;
};

export const BotonWhatsApp = ({ sx }) => (
  <Button
    data-testid="aviso-whatsapp"
    component="a"
    href={WHATSAPP_SOPORTE}
    target="_blank"
    rel="noopener noreferrer"
    startIcon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
    sx={{ ...botonTextoSx, color: "var(--color-success)", "&:hover": { backgroundColor: "rgba(var(--color-success-rgb), 0.12)" }, ...sx }}
  >
    Escríbeme por WhatsApp
  </Button>
);

const AvisoPagoDialog = ({ aviso, open, onCerrar }) => {
  const theme = useTheme();
  const pantallaCompleta = useMediaQuery(theme.breakpoints.down("sm"));
  const visto = useRef(false);
  const ahora = useAhora(open);
  useEffect(() => {
    if (open && !visto.current) {
      visto.current = true;
      enviarEventoAviso("visto");
    }
  }, [open]);
  if (!aviso) return null;

  // `pago_en_proceso` (decisión de Sebastián 27-oct): ya tiene tarjeta y el cobro del día de pago no
  // se confirmó; Flow lo reintenta 3 días y recién ahí se corta. No hay nada que pagar acá.
  const enProceso = aviso.fase === "pago_en_proceso";
  // Revisor #1809 v1 (falla 3): el texto sale de la fase Y de si el día de pago ya pasó, nunca fijo.
  // Desde la franja de «Estoy de turno» (o con el corte apagado, días después del día de pago) el
  // popup decía «Tu plan se renueva el 1 de noviembre» o «si mañana no está, se pausa», ya falsos.
  const hoyIso = new Date(ahora).toLocaleDateString("en-CA", { timeZone: "America/Santiago" });
  const diaPasado = Boolean(aviso.dia_pago) && aviso.dia_pago.slice(0, 10) < hoyIso;
  const pendiente = !enProceso && (aviso.fase === "turno_extra" || aviso.fase === "bloqueo" || diaPasado);
  const esDiaDePago = (aviso.fase === "dia_de_pago" && !diaPasado) || enProceso;
  const corta = aviso.cortar !== false;
  const dia = fechaDiaMes(aviso.dia_pago);
  const titulo =
    aviso.titulo ||
    (enProceso
      ? `Tu cobro del ${dia} no se confirmó todavía`
      : pendiente
        ? `Tu pago del ${dia} está pendiente`
        : esDiaDePago
          ? "Hoy es tu día de pago"
          : `Tu plan se renueva el ${dia}`);
  const texto =
    aviso.mensaje ||
    (enProceso
      ? "Flow lo reintenta solo en los próximos días. Si cambiaste o se venció tu tarjeta, escríbeme y lo resolvemos. Tu trabajo no se borra."
      : pendiente
        ? aviso.fase === "turno_extra"
          ? "Registra tu tarjeta antes de las 9:00 para que el acceso no se vuelva a pausar. Tu trabajo no se borra."
          : "Registra tu tarjeta y sigues sin cortes. Tu trabajo no se borra."
        : esDiaDePago
          ? corta
            ? "Registra tu tarjeta hoy para seguir sin cortes. Si mañana no está, el acceso se pausa hasta que pagues. Tu trabajo no se borra."
            : "Registra tu tarjeta hoy y el cobro queda automático cada mes."
          : `Registra tu tarjeta antes del ${dia} y sigues sin cortes. Desde ahí el cobro es automático cada mes, en esa fecha.`);
  const falta = (esDiaDePago && corta) || enProceso ? faltaPara(aviso.plazo_hasta, ahora) : "";

  const cerrar = () => {
    enviarEventoAviso("cerrado");
    onCerrar();
  };

  return (
    <Dialog
      open={open}
      onClose={cerrar}
      fullScreen={pantallaCompleta}
      maxWidth="xs"
      fullWidth
      aria-label={titulo}
      data-testid="aviso-pago"
      data-fase={aviso.fase}
      // Debajo de la pantalla de bloqueo (20000) y del retorno de Flow (20001).
      sx={{ zIndex: 19990 }}
      PaperProps={{
        sx: {
          backgroundColor: "var(--bg-elevated)",
          backgroundImage: "none",
          border: pantallaCompleta ? 0 : "1px solid var(--border-soft)",
          borderRadius: pantallaCompleta ? 0 : "16px",
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 2 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              flex: "none",
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              color: esDiaDePago ? "var(--color-warning)" : "var(--color-primary-soft)",
              backgroundColor: esDiaDePago ? "rgba(var(--color-warning-rgb), 0.14)" : "rgba(var(--color-primary-rgb), 0.14)",
              border: esDiaDePago
                ? "1px solid rgba(var(--color-warning-rgb), 0.35)"
                : "1px solid rgba(var(--color-primary-rgb), 0.35)",
            }}
          >
            {esDiaDePago ? <AccessAlarmOutlinedIcon sx={{ fontSize: 22 }} /> : <EventAvailableOutlinedIcon sx={{ fontSize: 22 }} />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography data-testid="aviso-titulo" sx={{ fontSize: 19, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1.25 }}>
              {titulo}
            </Typography>
            <Typography data-testid="aviso-texto" sx={{ fontSize: 13, color: "var(--text-secondary)", mt: 0.75, lineHeight: 1.5 }}>
              {texto}
            </Typography>
            {falta && (
              <Box
                data-testid="aviso-falta"
                sx={{
                  display: "inline-flex",
                  mt: 1.25,
                  px: 1.25,
                  py: 0.4,
                  borderRadius: "999px",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--color-warning)",
                  backgroundColor: "rgba(var(--color-warning-rgb), 0.12)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                Quedan {falta} para el corte
              </Box>
            )}
          </Box>
        </Box>
        {!enProceso && (
          <PlanesYPago
            titulo=""
            modoAviso
            onPagar={() => enviarEventoAviso("pagar")}
            onListo={onCerrar}
          />
        )}
        {!enProceso && aviso.jornadas != null && (
          <Typography data-testid="aviso-jornadas" sx={{ fontSize: 12, color: "var(--text-muted)", mt: 1 }}>
            Incluye <JornadasAlMes jornadas={aviso.jornadas} jornadasPlan={aviso.jornadas_plan} />.
          </Typography>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mt: 2, flexWrap: "wrap" }}>
          {aviso.whatsapp ? <BotonWhatsApp sx={{ ml: -1 }} /> : <span />}
          <Button data-testid="aviso-ahora-no" variant="outlined" onClick={cerrar} sx={botonGhostSx}>
            Ahora no
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AvisoPagoDialog;
